import {AsyncSupport, logger, GlobalThemeConfiguration} from "@zoovu/runner-browser-api";
import {CustomFontSelection} from "../configuration";
import {mapFontType} from "./font-types";

/**
 * A simple throttle guard for font loading.
 */
class LoadedFonts {

    private static DELETE_GUARD_MS = 5000;

    private fontLoadGuard: Set<string> = new Set();
    private fontLoadMonitor: string[] = [];

    public shallLoad(fontName: string): boolean {
        return !this.fontLoadGuard.has(fontName);
    }

    public loading(fontName: string): void {
        this.fontLoadGuard.add(fontName);
        this.fontLoadMonitor.push(fontName);

        setTimeout(() => {
            this.fontLoadGuard.delete(fontName);
        }, LoadedFonts.DELETE_GUARD_MS);
    }

    public loadFinished(fontName: string) {
        this.fontLoadMonitor.pop();
    }

    public async idle(): Promise<void> {
        do {
            await AsyncSupport.wait(100);
        } while (this.fontLoadMonitor.length > 0);
    }

    public reset(): void {
        this.fontLoadGuard.clear();
        this.fontLoadMonitor = [];
    }
}

const loadedFonts = new LoadedFonts();

export function loadFonts(fontSelection: CustomFontSelection, globalThemeConfiguration?: GlobalThemeConfiguration) {
    if (fontSelection.userLibraryFontUrl != null) {
        loadCustomFont(fontSelection.userLibraryFontName, fontSelection.userLibraryFontUrl);
    } else if (fontSelection.selected) {
        const selectedFont = fontSelection.selected;
        loadBuiltInFonts(mapFontType(selectedFont), selectedFont, [], globalThemeConfiguration).catch((error) => {
            logger.warn(error);
        });
    }
}

export async function loadingFontsIdle(): Promise<void> {
    await document["fonts"].ready;
    await loadedFonts.idle();
}

export function resetFontsLoadingMonitor(): void {
    loadedFonts.reset();
}

/**
 * Function tries to load for flavours of the requested font: light, regular, semi-bold and bold.
 * If font variant does not exists an error is logged and variant is skipped.
 *
 * @param fontType: sans-serif/serif/monospace
 * @param fontName family name
 * @param fileExtensions TODO: not used yet
 * @param globalThemeConfiguration used to provide fonts storage url
 */
async function loadBuiltInFonts(
    fontType: string,
    fontName: string,
    fileExtensions: ReadonlyArray<string>, // TODO: WBC-40 Add support for more font files formats
    globalThemeConfiguration?: GlobalThemeConfiguration) {

    if (!loadedFonts.shallLoad(fontName)) {
        return;
    }

    loadedFonts.loading(fontName);

    const fontsStoreBaseUrl = getFontsStoreBaseUrl(globalThemeConfiguration);
    const fontFamilyStorageUrl = `${fontsStoreBaseUrl}${fontType}/${fontName}`;

    await addFont(fontName, `${fontFamilyStorageUrl}-Light.ttf`, 300);
    await addFont(fontName, `${fontFamilyStorageUrl}-Regular.ttf`, 400);
    await addFont(fontName, `${fontFamilyStorageUrl}-SemiBold.ttf`, 600);
    await addFont(fontName, `${fontFamilyStorageUrl}-Bold.ttf`, 800);

    loadedFonts.loadFinished(fontName);
}

async function loadCustomFont(fontName: string, fontUrl: string) {
    if (!loadedFonts.shallLoad(fontName)) {
        return;
    }

    loadedFonts.loading(fontName);

    await addFont(fontName, fontUrl, 400);

    loadedFonts.loadFinished(fontName);
}

async function addFont(fontName: string, fontUrl: string, weight: number) {
    // @ts-ignore
    const fontDefinition = new FontFace(fontName, `url(${fontUrl})`, { weight });
    // @ts-ignore
    try { document.fonts.add(await fontDefinition.load()); } catch (e) { warn(e); }
}

function getFontsStoreBaseUrl(globalThemeConfiguration: GlobalThemeConfiguration) {
    if (globalThemeConfiguration && globalThemeConfiguration.fontsStoreBaseUrl) {
        return globalThemeConfiguration.fontsStoreBaseUrl.endsWith("/") ?
            globalThemeConfiguration.fontsStoreBaseUrl :
            `${globalThemeConfiguration.fontsStoreBaseUrl}/`;
    }
    return "https://assets-eu-runner.zoovu.com/core-js/themes/assets/fonts/";
}

function warn(e) {
    logger.warn(e);
}
